import {
  Button,
  Table,
  Modal,
  DeleteModal,
} from "@telluscompany/design-system";
import { useSearchParams } from "react-router-dom";
import { PlusCircle } from "lucide-react";
import { useLocalization } from "@i18n";
import type { PaginationMeta } from "@shared/api";
import { Gs3TablePaginator } from "@shared/components";
import { EMPTY_PAGINATION_META } from "@shared/constants/pagination.constants";
import { useUserContext } from "@shared/contexts";
import { useUpdateTableUserPreferences } from "@shared/hooks";
import { useCrudModals, useAdminAccessQuery } from "@shared/hooks";

import {
  useCreateAdminUserMutation,
  useDeleteAdminUserMutation,
  useUpdateAdminUserMutation,
  type ListAdminUsersResponseItem,
} from "../../api/admin-user";
import {
  AdminUserFormData,
  CreateAdminUserSubmitData,
} from "../../schemas";
import { AdminUsersActionMenu } from "./AdminUsersActionMenu";
import { AdminUsersFilter } from "./AdminUsersFilter";
import { CreateAdminUserForm } from "./CreateAdminUserForm";
import { EditAdminUserForm } from "./EditAdminUserForm";

interface AdminUsersTabProps {
  users: ListAdminUsersResponseItem[];
  isLoading?: boolean;
  onFilter: (filters: Record<string, unknown>) => void;
  onClearFilter: () => void;
  initialFilters?: Record<string, string>;
  paginatedMeta?: PaginationMeta;
}

const AdminUsersTab = ({
  users,
  onFilter,
  onClearFilter,
  initialFilters,
  paginatedMeta,
}: AdminUsersTabProps) => {
  const { translate } = useLocalization();
  const { user: me } = useUserContext();
  const updateTableUserPreferences = useUpdateTableUserPreferences(me?.userPreferences);
  const { hasAdminAccess } = useAdminAccessQuery();

  const {
    modals,
    selectedItem: selectedUser,
    openCreate,
    openEdit,
    openDelete,
    closeCreate,
    closeEdit,
    closeDelete,
  } = useCrudModals<ListAdminUsersResponseItem>();

  const { mutate: createAdminUser } = useCreateAdminUserMutation();
  const { mutate: updateAdminUser } = useUpdateAdminUserMutation();
  const { mutate: deleteAdminUser, isPending: isDeleting } =
    useDeleteAdminUserMutation();

  // canWrite reflete a regra que o backend aplica (assertNotOwnerViewer +
  // EnsureTenantAdminAccess). Hoje usamos me.role (master) + hasAdminAccess
  // como proxy porque o front ainda não recebe o tenantRole canônico — ver
  // TODO em @shared/hooks/useAdminAccessQuery.ts. Apoia-se no espelhamento
  // 1:1 master↔tenant de owner/ownerViewer documentado em docs/permissions.md;
  // quando o backend expuser tenantRole, trocar por:
  //   canWrite = tenantRole !== 'ownerViewer' && hasAdminAccess
  const canWrite =
    me?.role !== "ownerViewer" && (me?.role === "owner" || hasAdminAccess);

  const handleCreate = (data: CreateAdminUserSubmitData) => {
    createAdminUser(data, {
      onSuccess: () => closeCreate(),
    });
  };

  const handleEdit = (data: AdminUserFormData) => {
    if (!selectedUser) return;

    updateAdminUser(
      {
        id: selectedUser.id,
        name: data.name,
        phone: data.phone,
        department: data.department || undefined,
        position: data.position || undefined,
      },
      {
        onSuccess: () => closeEdit(),
        onError: () => closeEdit(),
      },
    );
  };

  const handleDelete = () => {
    const id = selectedUser?.id;
    if (!id) return;

    deleteAdminUser(id, {
      onSuccess: () => closeDelete(),
      onError: () => closeDelete(),
    });
  };

  const createActionMenu = (rowData: {
    rowData: ListAdminUsersResponseItem;
  }) => {
    const rowUser = rowData.rowData;

    return (
      <AdminUsersActionMenu
        user={rowUser}
        onEdit={() => openEdit(rowUser)}
        onDelete={() => openDelete(rowUser)}
      />
    );
  };

  const tableEqualColumnsClassName = canWrite
    ? "[&_table]:w-full [&_thead_tr_th:not(:last-child)]:w-[calc((100%-2.5rem)/5)] [&_tbody_tr_td:not(:last-child)]:w-[calc((100%-2.5rem)/5)]"
    : "[&_table]:w-full [&_thead_tr_th]:w-1/5 [&_tbody_tr_td]:w-1/5";

  return (
    <div className={`p-4 ${tableEqualColumnsClassName}`}>
      <Table<ListAdminUsersResponseItem>
        userId={me?.id ?? ""}
        tableId="admin-users-table"
        usersPreferencesFromServer={me?.userPreferences}
        updateUsersPreferencesOnServer={updateTableUserPreferences}
        useSearchParamsHook={useSearchParams}
        enableSortByUrl
        TableActionMenu={
          canWrite
            ? (createActionMenu as React.ComponentType)
            : undefined
        }
        TableButtons={
          canWrite ? (
            <Button
              size="small"
              variant="primary"
              onClick={openCreate}
              iconLeft={<PlusCircle />}
            >
              {translate("admin.users.button.new")}
            </Button>
          ) : undefined
        }
        TableFilter={() => (
          <AdminUsersFilter
            onFilter={onFilter}
            onClear={onClearFilter}
            initialFilters={initialFilters}
          />
        )}
        TablePaginator={
          <Gs3TablePaginator
            meta={paginatedMeta ?? EMPTY_PAGINATION_META}
            useUrlState
            useSearchParamsHook={useSearchParams}
          />
        }
        draggable
        enableTableColumnsSelector
        filterButtonText={translate("admin.users.table.filterButtonText")}
        handleClickRow={() => {}}
        tableColumns={[
          {
            key: "name",
            label: translate("admin.users.table.column.name"),
            sortType: "text",
          },
          {
            key: "email",
            label: translate("admin.users.table.column.email"),
            sortType: "text",
          },
          {
            key: "userType",
            label: translate("admin.users.table.column.userType"),
            sortType: "text",
          },
          {
            key: "department",
            label: translate("admin.users.table.column.department"),
            sortType: "text",
          },
          {
            key: "position",
            label: translate("admin.users.table.column.position"),
            sortType: "text",
          },
        ]}
        tableData={users.map(
          (u) =>
            ({
              ...u,
              userType: translate(`admin.users.userType.${u.userType}`),
              department: u.department ?? "—",
              position: u.position ?? "—",
            }) as { [key: string]: unknown; color?: string },
        )}
      />

      <Modal
        open={modals.create}
        content={
          <CreateAdminUserForm
            onSubmit={handleCreate}
            onCancel={closeCreate}
          />
        }
        onClose={closeCreate}
        title={translate("admin.users.modal.title.create")}
      />

      <Modal
        open={modals.edit}
        content={
          <EditAdminUserForm
            onSubmit={handleEdit}
            onCancel={closeEdit}
            userId={selectedUser?.id ?? undefined}
          />
        }
        onClose={closeEdit}
        title={translate("admin.users.modal.title.edit")}
      />

      <DeleteModal
        open={modals.delete}
        onClose={closeDelete}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
        itemDescription={selectedUser?.name}
        title={translate("common.deleteModalTitle")}
        message={translate("common.deleteModalItemMessage")}
        alertMessage={translate("common.deleteModalAlertMessage")}
        confirmButtonText={translate("common.delete")}
        cancelButtonText={translate("common.cancel")}
        deletingButtonText={translate("common.deleting")}
      />
    </div>
  );
};

export default AdminUsersTab;
